import axios from 'axios';
import * as turf from "@turf/turf";
import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { wfsCache, debounce, bboxOverlap, requestManager } from '../../utils/wfsCache';
import { 
  createSingleTenementPopupTable, 
  displayTenementHolder, 
  getTenStatusFilter, 
  getSurveyedStatusfilter, 
  getDeadSplFilter 
} from './TenementOverlay';

import {
    Popup,
    Source,
    Layer,
    useMap,
    Marker
} from 'react-map-gl/maplibre';

const GoldFinderGeoserver = axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER,
    timeout: 30000
});

// Debounced query function to prevent excessive API calls
export const debouncedQueryTenementWFS = debounce(queryTenementWFSLibreOptimized, 300);

/**
 * Optimized tenement WFS query with caching and request management
 */
export function queryTenementWFSLibreOptimized(bboxString, tenementFilters, setStateGeoJSON, retryCount = 0) {
    const cacheKey = wfsCache.generateKey(bboxString, tenementFilters, 'tenements');
    
    // Check cache first
    const cachedResult = wfsCache.get(cacheKey);
    if (cachedResult) {
        setStateGeoJSON(cachedResult);
        return Promise.resolve(cachedResult);
    }

    // Create abort controller for this request
    const controller = requestManager.createController('tenements');

    const getTenStatusFilter = () => {
        const filters = [];
        if (tenementFilters.includeLive) filters.push('LIVE');
        if (tenementFilters.includePending) filters.push('PENDING');
        if (tenementFilters.includeDead) filters.push('Dead');

        if (filters.length === 0) return `tenstatus='potato' and`;
        
        return filters.length === 1 
            ? `tenstatus='${filters[0]}' and`
            : `tenstatus in ('${filters.join(`','`)}') and`;
    };

    const getSurveyedStatusfilter = () => {
        const filters = [];
        if (tenementFilters.includeSurveyed) {
            filters.push('SURVEYED', 'Surveyed');
        }
        if (tenementFilters.includeUnsurveyed) {
            filters.push('UNSURVEYED', 'Unsurveyed');
        }

        return filters.length === 0 ? "" : `survstatus in ('${filters.join(`','`)}')`;
    };

    const getDeadSplFilter = () => {
        return tenementFilters.deadSpls 
            ? `or (type = 'SPECIAL PROSPECTING LICENCE' and tenstatus= 'Dead')`
            : "";
    };

    const tenementTypes = tenementFilters.types.map(element => element.value);

    const holderNameFilter = tenementFilters.holderNameLike === '' 
        ? '' 
        : `and (${tenementFilters.holderNameLike.split(' ').map((e, index) => 
            `${index !== 0 ? 'and' : ''} holdername ILIKE '%${e}%'`
          ).join(' ')})`;

    const standardFilter = `
        ${tenementFilters.includeSurveyed || tenementFilters.includeUnsurveyed ? "and" : ""}
        ${getSurveyedStatusfilter()}
        and
        ((
        ${getTenStatusFilter()}
        type in ('${tenementTypes.join(`','`)}')
        )
        ${getDeadSplFilter()}
        )
        and
        startdate AFTER ${tenementFilters.startDate.toISOString()}
        and 
        enddate before ${tenementFilters.endDate.toISOString()}
        and
        grantdate ${tenementFilters.grantDateBeforeAfter === 'AFTER' ? "AFTER" : 'BEFORE'} ${tenementFilters.grantDate.toISOString()}
        ${tenementFilters.grantedOnly ? "and grantdate BEFORE 2999-01-01T00:00:00.000Z" : ""}
        ${holderNameFilter}
    `;

    const expiringSoonFilter = `
        and
        (
        tenstatus = 'PENDING'
        or
        (
        tenstatus = 'LIVE'
        and 
        type in ('${tenementTypes.join(`','`)}')
        and 
        enddate before ${tenementFilters.endDate.toISOString()}
        )
        )
    `;

    const ecqlFilter = `
        BBOX(geom,${bboxString})
        ${tenementFilters.expiringSoonEnabled ? expiringSoonFilter : standardFilter}
    `;

    return GoldFinderGeoserver.get("/ne/ows?", {
        signal: controller.signal,
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_tenements',
            maxFeatures: 99999,
            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            cql_filter: ecqlFilter
        }
    }).then((response) => {
        if (response.data.features === undefined) return;

        // Process date objects in a more efficient way
        const processedFeatures = response.data.features.map(tenement => ({
            ...tenement,
            properties: {
                ...tenement.properties,
                startdate_obj: new Date(tenement.properties.startdate),
                enddate_obj: new Date(tenement.properties.enddate),
                grantdate_obj: new Date(tenement.properties.grantdate)
            }
        }));

        response.data.features = processedFeatures;

        // Apply client-side filters more efficiently
        if (tenementFilters.expiringSoonEnabled) {
            if (tenementFilters.ignoreFirstTermPl) {
                response.data.features = response.data.features.filter((ten) => {
                    if (ten.properties.type === "PROSPECTING LICENCE") {
                        const ageDifMs = ten.properties.enddate_obj - ten.properties.grantdate_obj;
                        const years = Math.abs(new Date(ageDifMs).getUTCFullYear() - 1970);
                        return years > 4;
                    }
                    return true;
                });
            }

            if (tenementFilters.overridePendingTenementDisplay) {
                response.data.features = response.data.features.filter((ten) => {
                    if (ten.properties.tenstatus === "PENDING") {
                        return response.data.features.some((f) => {
                            if (f.properties.tenstatus !== "PENDING") {
                                return turf.intersect(turf.featureCollection([ten, f])) !== null;
                            }
                            return false;
                        });
                    }
                    return true;
                });
            }
        }

        // Cache the result
        wfsCache.set(cacheKey, response.data);
        setStateGeoJSON(response.data);
        return response.data;
    }).catch((error) => {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
            console.error('Tenement WFS query error:', error);
        }
        
        // Don't re-throw canceled errors
        if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            return null;
        }
        
        // Retry on timeout or network errors
        const maxRetries = 10;
        const baseDelay = 1000;
        if ((error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') && retryCount < maxRetries) {
            const delay = baseDelay * Math.pow(2, retryCount);
            console.log(`Retrying tenement query in ${delay}ms (attempt ${retryCount + 1}/${maxRetries + 1})`);
            
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(queryTenementWFSLibreOptimized(bboxString, tenementFilters, setStateGeoJSON, retryCount + 1));
                }, delay);
            });
        }
        
        // For other errors or max retries exceeded, return empty GeoJSON to keep UI functional
        const emptyResult = {
            type: "FeatureCollection",
            features: []
        };
        
        // Set empty result in state to prevent UI breakage
        setStateGeoJSON(emptyResult);
        
        // Return empty result instead of throwing error
        return emptyResult;
    });
}

/**
 * Optimized TenementOverlay component with performance improvements
 */
export function TenementOverlayMapLibreOptimized({ 
    tenementFilters, 
    overlayVisibility, 
    selectedTenement, 
    geoJson, 
    setGeojson 
}) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "Mining_Tenements_DMIRS_003",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4283" } },
        "features": []
    };

    const { goldfindermap } = useMap();
    const lastBboxRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    
    // Create point geometries for labels from polygon centroids
    const tenementLabelsGeoJson = useMemo(() => {
        if (!geoJson || !geoJson.features || geoJson.features.length === 0) {
            return emptyGeoJson;
        }
        
        const labelFeatures = geoJson.features.map(feature => {
            const centerOfMass = turf.centerOfMass(feature);
            return {
                type: 'Feature',
                geometry: centerOfMass.geometry,
                properties: feature.properties
            };
        });
        
        const result = {
            type: 'FeatureCollection',
            features: labelFeatures
        };
        
        // Debug logging (can be removed in production)
        
        return result;
    }, [geoJson]);

    // Memoize layer configurations to prevent unnecessary re-renders
    const layerConfigs = useMemo(() => ({
        liveLayerFill: {
            id: 'live-tenements',
            minzoom: 8,
            type: 'fill',
            filter: ['==', 'tenstatus', 'LIVE'],
            paint: { 'fill-color': '#008000', 'fill-opacity': 0.2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        liveLayerOutline: {
            id: 'live-tenements-outline',
            minzoom: 8,
            type: 'line',
            filter: ['==', 'tenstatus', 'LIVE'],
            paint: { 'line-color': "#008000", 'line-width': 2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        pendingLayerFill: {
            id: 'pending-tenements',
            minzoom: 8,
            type: 'fill',
            filter: ['==', 'tenstatus', 'PENDING'],
            paint: { 'fill-color': '#0000FF', 'fill-opacity': 0.2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        pendingLayerOutline: {
            id: 'pending-tenements-outline',
            minzoom: 8,
            type: 'line',
            filter: ['==', 'tenstatus', 'PENDING'],
            paint: { 'line-color': "#0000FF", 'line-width': 2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        deadLayerFill: {
            id: 'dead-tenements',
            minzoom: 8,
            type: 'fill',
            filter: ['all', ['!=', 'type', 'SPECIAL PROSPECTING LICENCE'], ['==', 'tenstatus', 'Dead']],
            paint: { 'fill-color': '#808080', 'fill-opacity': 0.2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        deadLayerOutline: {
            id: 'dead-tenements-outline',
            minzoom: 8,
            type: 'line',
            filter: ['all', ['!=', 'type', 'SPECIAL PROSPECTING LICENCE'], ['==', 'tenstatus', 'Dead']],
            paint: { 'line-color': "#808080", 'line-width': 2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        deadSPLLayerFill: {
            id: 'dead-spl-tenements',
            minzoom: 8,
            type: 'fill',
            filter: ['all', ['==', 'type', 'SPECIAL PROSPECTING LICENCE'], ['==', 'tenstatus', 'Dead']],
            paint: { 'fill-color': '#ff0000', 'fill-opacity': 0.2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        deadSPLLayerOutline: {
            id: 'dead-spl-tenements-outline',
            minzoom: 8,
            type: 'line',
            filter: ['all', ['==', 'type', 'SPECIAL PROSPECTING LICENCE'], ['==', 'tenstatus', 'Dead']],
            paint: { 'line-color': "#ff0000", 'line-width': 2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        selectedTenementFill: {
            id: 'selected-tenement-fill',
            minzoom: 8,
            type: 'fill',
            filter: ['==', 'gid', selectedTenement.properties.gid || 0],
            paint: { 'fill-color': '#FFFF00', 'fill-opacity': 0.2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        selectedTenementOutline: {
            id: 'selected-tenement-outline',
            minzoom: 8,
            type: 'line',
            filter: ['==', 'gid', selectedTenement.properties.gid || 0],
            paint: { 'line-color': "#FFFF00", 'line-width': 2 },
            layout: { visibility: `${overlayVisibility.tenements ? "visible" : "none"}` }
        },
        // Symbol layer for tenement text labels
        tenementLabels: {
            id: 'tenement-labels',
            source: 'tenement-labels',
            minzoom: 10.6,
            type: 'symbol',
            layout: {
                'text-field': ['get', 'fmt_tenid'],
                'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
                'text-size': 12,
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true,
                visibility: `${overlayVisibility.tenements && overlayVisibility.tenementLabels ? "visible" : "none"}`
            },
            paint: {
                'text-color': [
                    'case',
                    ['==', ['get', 'tenstatus'], 'LIVE'], 'rgba(0,255,0,1)',
                    ['==', ['get', 'tenstatus'], 'PENDING'], 'rgba(100,150,255,1)',
                    ['all', ['==', ['get', 'type'], 'SPECIAL PROSPECTING LICENCE'], ['==', ['get', 'tenstatus'], 'Dead']], 'rgba(255,100,100,1)',
                    'rgba(255,255,255,1)'
                ],
                'text-halo-color': 'rgba(0,0,0,1)',
                'text-halo-width': 2
            }
        }
    }), [overlayVisibility.tenements, overlayVisibility.tenementLabels, selectedTenement.properties.gid]);

    // Optimized query function that checks for significant bbox changes
    const queryTenements = useCallback(async (bbox) => {
        if (!goldfindermap || goldfindermap.getZoom() <= 8) return;

        // Check if bbox has changed significantly
        if (lastBboxRef.current && bboxOverlap(bbox, lastBboxRef.current)) {
            return;
        }

        setIsLoading(true);
        try {
            const result = await debouncedQueryTenementWFS(bbox, tenementFilters, setGeojson);
            if (result !== null) {
                lastBboxRef.current = bbox;
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
                console.error('Error querying tenements:', error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [goldfindermap, tenementFilters, setGeojson]);

    // Effect for filter changes
    useEffect(() => {
        if (!goldfindermap) return;
        
        // Clear cache when filters change
        wfsCache.clearLayer('tenements');
        
        if (goldfindermap.getZoom() > 8) {
            const bounds = goldfindermap.getBounds();
            const wfsbbox = [
                bounds.getSouthWest().lng, 
                bounds.getSouthWest().lat, 
                bounds.getNorthEast().lng, 
                bounds.getNorthEast().lat
            ].join();
            
            queryTenements(wfsbbox);
        }
    }, [tenementFilters, queryTenements]);

    return (
        <>
            <Source id="tenements" type="geojson" data={geoJson}>
                <Layer {...layerConfigs.liveLayerFill} />
                <Layer {...layerConfigs.liveLayerOutline} />
                <Layer {...layerConfigs.pendingLayerFill} />
                <Layer {...layerConfigs.pendingLayerOutline} />
                <Layer {...layerConfigs.deadSPLLayerFill} />
                <Layer {...layerConfigs.deadSPLLayerOutline} />
                <Layer {...layerConfigs.deadLayerFill} />
                <Layer {...layerConfigs.deadLayerOutline} />
                <Layer {...layerConfigs.selectedTenementFill} />
                <Layer {...layerConfigs.selectedTenementOutline} />
            </Source>
            {/* Separate source for tenement labels using point geometries */}
            <Source id="tenement-labels" type="geojson" data={tenementLabelsGeoJson}>
                <Layer {...layerConfigs.tenementLabels} />
            </Source>
        </>
    );
}